import type { SearchFieldConfig, StructuredSearchState } from '../types'

// Reserved by the query string parser. A term the form treated as literal text has to
// stay literal once it is written into a query string.
const RESERVED = /([+\-=&|><!(){}[\]^"~*?:\\/])/g

function escapeTerm(value: string): string {
  return value.replace(RESERVED, '\\$1')
}

function escapePhrase(value: string): string {
  return value.replace(/(["\\])/g, '\\$1')
}

function fuzzySuffix(s: StructuredSearchState): string {
  if (!s.fuzzy || s.matchType === 'phrase') {
    return ''
  }
  return s.fuzzyEdits === 'auto' ? '~' : `~${s.fuzzyEdits}`
}

function renderTerms(s: StructuredSearchState): string {
  if (s.matchType === 'phrase') {
    return `"${escapePhrase(s.query.trim())}"`
  }

  const suffix = fuzzySuffix(s)
  const terms = s.query.trim().split(/\s+/).map((term) => `${escapeTerm(term)}${suffix}`)
  return terms.length > 1
    ? `(${terms.join(s.booleanAnd ? ' AND ' : ' OR ')})`
    : terms[0]
}

function aliasFor(fieldPath: string, fields: SearchFieldConfig[]): string {
  return fields.find((f) => f.fieldPath === fieldPath)?.syntaxAlias ?? fieldPath
}

/**
 * Render the form's search as the equivalent query string, so switching to query
 * syntax hands over what was built rather than an empty box.
 *
 * The date range is left out: query syntax has no date control and the field it would
 * name is per-view rather than part of the field list. Callers warn about that.
 */
export function structuredToQueryString(
  s: StructuredSearchState,
  fields: SearchFieldConfig[]
): string {
  const clauses: string[] = []
  const query = s.query.trim()

  if (query) {
    const missing = new Set(s.fieldExists.filter((fe) => !fe.exists).map((fe) => fe.field))
    const chosen = s.selectedFields.length > 0
      ? s.selectedFields
      : fields.filter((f) => f.defaultSelected).map((f) => f.fieldPath)
    const searchable = chosen.filter((f) => !missing.has(f))
    const targets = searchable.length > 0 ? searchable : chosen

    const terms = renderTerms(s)
    const perField = targets.map((fieldPath) => `${aliasFor(fieldPath, fields)}:${terms}`)
    const joined = perField.length > 1 ? `(${perField.join(' OR ')})` : perField[0] ?? ''

    if (joined) {
      clauses.push(s.boost > 1 ? `(${joined})^${s.boost}` : joined)
    }
  }

  for (const fe of s.fieldExists) {
    // The alias substitution only rewrites `alias:`, and here the field sits in the
    // value position, so this has to name the indexed path outright.
    const clause = `_exists_:${fe.field}`
    clauses.push(fe.exists ? clause : `NOT ${clause}`)
  }

  return clauses.join(' AND ')
}

export function dropsDateRange(s: StructuredSearchState): boolean {
  return !!(s.dateRange.from || s.dateRange.to)
}
